import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { User, WebStorageStateStore } from "oidc-client-ts";
import { AuthContextProps, useAuth } from "react-oidc-context";
import { httpPostGraphql } from "shared/api/httpClient";
import { useIsSupport } from "shared/hooks/useIsSupport";
import { fail } from "shared/utils/fns";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { Permission, PermissionLikeFragment } from "src/gql/graphql";
import { z } from "zod";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

type OidcStore = {
    config: {
        authority: string;
        client_id: string;
        redirect_uri: string;
        post_logout_redirect_uri: string;
        scope: string;
        userStore: WebStorageStateStore;
    };
    setRealm: (realm: string) => void;
    clearAccessToken: () => void;
    getAccessToken: () => string | undefined;
};

export const subdomain: string = import.meta.env["VITE_FALLBACK_REALM"] ?? window.location.hostname.split(".").at(0);

const userStoreDefault = {
    config: {
        authority: `${import.meta.env["VITE_KC_BASE_URL"]}/realms/${subdomain}`,
        client_id: "emns-web",
        redirect_uri: `${window.location.origin}/account/callback`,
        post_logout_redirect_uri: `${window.location.origin}/login`,
        scope: "openid profile email",
        userStore: new WebStorageStateStore({ store: window.localStorage }),
    },
};

export const useOIDCStore = create<OidcStore>()(
    devtools(
        // https://docs.pmnd.rs/zustand/integrations/persisting-store-data#options
        persist(
            immer((set, get) => {
                return {
                    ...userStoreDefault,
                    setRealm: (realm: string) => {
                        set((state) => {
                            state.config.authority = `${import.meta.env.VITE_KC_BASE_URL}/realms/${realm}`;
                        });
                    },
                    clearAccessToken() {
                        const {
                            config: { authority, client_id },
                        } = get();
                        localStorage.removeItem(`oidc.user:${authority}:${client_id}`);
                    },
                    getAccessToken() {
                        if (!!window.interop) {
                            return window.interop.getAccessToken();
                        }

                        const {
                            config: { authority, client_id },
                        } = get();
                        const raw = localStorage.getItem(`oidc.user:${authority}:${client_id}`);
                        if (raw == null) return undefined;

                        const parsed = z.object({ access_token: z.string() }).safeParse(JSON.parse(raw));
                        if (parsed.success) return parsed.data.access_token;
                        else return undefined;
                    },
                };
            }),
            {
                name: "emns.store.oidc",
                storage: createJSONStorage(() => localStorage),
                partialize: (store) => ({
                    config: store.config,
                }),
            },
        ),
        { enabled: import.meta.env.VITE_ENVIRONMENT !== "prod", name: "Auth" },
    ),
);

export function getUserId(auth: AuthContextProps): string {
    return (
        import.meta.env["VITE_USER_ID_OVERRIDE"] ?? auth.user?.profile.sub ?? window.interop?.getUserId() ?? fail("auth context is unset")
    );
}

export function useUserId() {
    const auth = useAuth();
    const { isSupport } = useIsSupport();
    return { userId: getUserId(auth), isSupport };
}

export function usePermissions() {
    const {
        data: { clientPermissions },
    } = useSuspenseQuery(oidcQueryOptions.clientPermissions);
    return { permissions: clientPermissions as PermissionLikeFragment };
}

export function permissionCheck(userPerms: PermissionLikeFragment, neededPerm: Permission, withId?: string): boolean {
    return (
        !!userPerms.customer.find((p) => p == neededPerm) ||
        !!userPerms.group.find((g) => (withId ? g.id == withId : true) && g.permissions.some((p) => p == neededPerm)) ||
        !!userPerms.topic.find((t) => (withId ? t.id == withId : true) && t.permissions.some((p) => p == neededPerm)) ||
        !!userPerms.ipaws.find((i) => (withId ? i.id == withId : true) && i.permissions.some((p) => p == neededPerm))
    );
}

export function usePermissionCheck(neededPerm: Permission, withId?: string) {
    const { permissions } = usePermissions();
    const { isSupport } = useIsSupport();
    return isSupport || permissionCheck(permissions, neededPerm, withId);
}

/** @public */
export function permissionsCheck(userPerms: PermissionLikeFragment, opts: [permission: Permission, withId?: string][]): boolean {
    return opts.reduce((isPassing, args) => isPassing && permissionCheck(userPerms, ...args), true);
}

/** @public */
export function usePermissionsCheck(opts: [permission: Permission, withId?: string][]) {
    const { permissions } = usePermissions();
    return opts.reduce((isPassing, args) => isPassing && permissionCheck(permissions, ...args), true);
}

const oidcQueryOptions = {
    clientPermissions: queryOptions({
        staleTime: Infinity,
        queryKey: qk("user", "permissions"),
        queryFn: () => httpPostGraphql(queries.clientPermissions, {}),
    }),
};

const queries = {
    permissionsFragment: graphql(`
        fragment PermissionLike on ClientPermissionNode {
            customer
            group {
                id
                permissions
            }
            topic {
                id
                permissions
            }
            ipaws {
                id
                permissions
            }
        }
    `),
    clientPermissions: graphql(`
        query Permissions {
            clientPermissions {
                ...PermissionLike
            }
        }
    `),
};

export function injectOidcSessionAttributes() {
    const oidcStorage = localStorage.getItem(
        `oidc.user:${useOIDCStore.getState().config.authority}:${useOIDCStore.getState().config.client_id}`,
    );

    if (!oidcStorage) {
        return {};
    }

    const { iss, sid, email, name } = User.fromStorageString(oidcStorage).profile;
    return {
        "account.iss": iss,
        "account.sid": sid,
        "account.email": email,
        "account.name": name,
    };
}
